import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Check, X, ArrowUp, ArrowDown, Languages, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  name: '', name_en: '', name_me: '', name_ru: '', name_sr: '',
};

const LANG_LABELS = [
  { key: 'name_en', label: 'EN' },
  { key: 'name_me', label: 'ME' },
  { key: 'name_ru', label: 'RU' },
  { key: 'name_sr', label: 'SR' },
];

const CategoryManagement = ({ token, onChange }) => {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchCats = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/menu-categories`);
      setCats(res.data || []);
    } catch { toast.error('Kategoriler yüklenemedi'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCats(); }, []);

  const notifyParent = () => { if (onChange) onChange(); };

  const handleAdd = async () => {
    const name = (form.name || '').trim();
    if (!name) return toast.error('Kategori adı gerekli');
    setSaving(true);
    try {
      await axios.post(`${API}/admin/menu-categories`, form, { headers });
      toast.success('Kategori eklendi');
      setForm(emptyForm);
      setShowAdd(false);
      await fetchCats();
      notifyParent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eklenemedi');
    } finally { setSaving(false); }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditForm({
      name: c.name || '',
      name_en: c.name_en || '',
      name_me: c.name_me || '',
      name_ru: c.name_ru || '',
      name_sr: c.name_sr || '',
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditForm(emptyForm); };

  const saveEdit = async () => {
    const new_name = (editForm.name || '').trim();
    if (!new_name) return toast.error('Ad boş olamaz');
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/menu-categories/${editingId}`, {
        new_name,
        name_en: editForm.name_en || null,
        name_me: editForm.name_me || null,
        name_ru: editForm.name_ru || null,
        name_sr: editForm.name_sr || null,
      }, { headers });
      const moved = res.data?.renamed_items || 0;
      toast.success(moved ? `Güncellendi. ${moved} ürün taşındı.` : 'Güncellendi');
      cancelEdit();
      await fetchCats();
      notifyParent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Güncellenemedi');
    } finally { setSaving(false); }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`"${c.name}" kategorisini silmek istediğinizden emin misiniz?`)) return;
    try {
      await axios.delete(`${API}/admin/menu-categories/${c.id}`, { headers });
      toast.success('Kategori silindi');
      await fetchCats();
      notifyParent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silinemedi');
    }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= cats.length) return;
    const next = [...cats];
    const tmp = next[idx];
    next[idx] = next[target];
    next[target] = tmp;
    const items = next.map((c, i) => ({ id: c.id, display_order: i }));
    setCats(next.map((c, i) => ({ ...c, display_order: i })));
    try {
      await axios.post(`${API}/admin/menu-categories/reorder`, { items }, { headers });
      notifyParent();
    } catch { toast.error('Sıralama kaydedilemedi'); fetchCats(); }
  };

  const handleAiTranslate = async (targetForm, setter) => {
    const name = (targetForm.name || '').trim();
    if (!name) return toast.error('Önce Türkçe adı girin');
    try {
      const res = await axios.post(`${API}/admin/catering/translate`, {
        name, category: 'kategori'
      }, { headers });
      const t = res.data || {};
      setter({
        ...targetForm,
        name_en: targetForm.name_en || t.name_en || '',
        name_me: targetForm.name_me || t.name_me || '',
        name_ru: targetForm.name_ru || t.name_ru || '',
        name_sr: targetForm.name_sr || t.name_sr || '',
      });
      toast.success('Çeviri tamamlandı');
    } catch { toast.error('Çeviri başarısız'); }
  };

  if (loading) return <div className="text-center py-6 text-charcoal/60">Yükleniyor...</div>;

  return (
    <div className="space-y-3" data-testid="category-management-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm text-charcoal/60">{cats.length} kategori. Sıralama, müşteri menüsünü de etkiler.</p>
        {!showAdd && (
          <Button size="sm" onClick={() => setShowAdd(true)}
            className="bg-basil text-white hover:bg-basil/90 rounded-full"
            data-testid="cat-add-btn">
            <Plus className="w-4 h-4 mr-1" />Yeni Kategori
          </Button>
        )}
      </div>

      {showAdd && (
        <div className="rounded-xl border border-basil/30 bg-basil/5 p-3 space-y-2" data-testid="cat-add-form">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-basil">Yeni Kategori</span>
            <button onClick={() => { setShowAdd(false); setForm(emptyForm); }} className="text-charcoal/50 hover:text-charcoal">
              <X className="w-4 h-4" />
            </button>
          </div>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Türkçe ad (örn. İçecekler)" data-testid="cat-add-name" />
          <div className="grid grid-cols-2 gap-2">
            {LANG_LABELS.map(({ key, label }) => (
              <Input key={key} value={form[key] || ''}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                placeholder={label} data-testid={`cat-add-${key}`} />
            ))}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => handleAiTranslate(form, setForm)}
              className="border-amber-300 text-amber-700 rounded-full" data-testid="cat-add-ai-translate">
              <Languages className="w-3 h-3 mr-1" />AI Çevir
            </Button>
            <Button size="sm" onClick={handleAdd} disabled={saving}
              className="ml-auto bg-basil text-white rounded-full" data-testid="cat-add-save">
              {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
              Kaydet
            </Button>
          </div>
        </div>
      )}

      <div className="divide-y divide-stone-200 border border-stone-200 rounded-xl overflow-hidden">
        {cats.map((c, idx) => {
          const isEditing = editingId === c.id;
          return (
            <div key={c.id} className="p-2.5 bg-white" data-testid={`cat-row-${c.id}`}>
              {isEditing ? (
                <div className="space-y-2">
                  <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    placeholder="Türkçe" data-testid={`cat-edit-name-${c.id}`} />
                  <div className="grid grid-cols-2 gap-2">
                    {LANG_LABELS.map(({ key, label }) => (
                      <Input key={key} value={editForm[key] || ''}
                        onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })}
                        placeholder={label} />
                    ))}
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => handleAiTranslate(editForm, setEditForm)}
                      className="border-amber-300 text-amber-700 rounded-full text-xs">
                      <Languages className="w-3 h-3 mr-1" />AI
                    </Button>
                    <Button size="sm" variant="outline" onClick={cancelEdit} className="rounded-full text-xs">Vazgeç</Button>
                    <Button size="sm" onClick={saveEdit} disabled={saving}
                      className="ml-auto bg-basil text-white rounded-full text-xs" data-testid={`cat-edit-save-${c.id}`}>
                      {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}Kaydet
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="flex flex-col gap-0.5">
                    <button onClick={() => move(idx, -1)} disabled={idx === 0}
                      className="p-0.5 rounded hover:bg-stone-100 disabled:opacity-30" data-testid={`cat-up-${c.id}`}>
                      <ArrowUp className="w-3 h-3 text-charcoal/60" />
                    </button>
                    <button onClick={() => move(idx, +1)} disabled={idx === cats.length - 1}
                      className="p-0.5 rounded hover:bg-stone-100 disabled:opacity-30" data-testid={`cat-down-${c.id}`}>
                      <ArrowDown className="w-3 h-3 text-charcoal/60" />
                    </button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-charcoal truncate">{c.name}</p>
                    <p className="text-[11px] text-charcoal/50 truncate">
                      {[c.name_en, c.name_me, c.name_ru, c.name_sr].filter(Boolean).join(' · ') || 'Diğer diller boş'}
                    </p>
                  </div>
                  <button onClick={() => startEdit(c)}
                    className="p-1.5 rounded hover:bg-stone-100 text-charcoal/60" data-testid={`cat-edit-btn-${c.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => handleDelete(c)}
                    className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`cat-delete-btn-${c.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CategoryManagement;
